import { isEqual } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { useDeepMemo } from "./deep-memo.js";
import { usePolling, useRetryWithBackoff } from "./timers.js";
function useSharedRequestDeps(url, init, options) {
    const requestDeps = [
        url,
        init,
        options?.pause,
        options?.fetchFn
    ];
    const request = useDeepMemo(()=>{
        if (options?.pause) return;
        return createRequest(url, init, options?.fetchFn);
    }, requestDeps);
    const [requestState, setRequestState] = useState({
        request,
        requestDeps
    });
    const [resultState, setResultState] = useState({
        loading: void 0 !== request,
        response: void 0,
        error: void 0
    });
    let currentResult = resultState;
    if (request !== requestState.request && !isEqual(requestState.requestDeps, requestDeps)) {
        currentResult = {
            ...resultState,
            loading: void 0 !== request
        };
        setRequestState({
            request,
            requestDeps
        });
        setResultState(currentResult);
    }
    useEffect(()=>{
        if (void 0 === requestState.request) return void setResultState((current)=>({
                ...current,
                loading: false
            }));
        const { abortController, executeRequest } = requestState.request;
        async function updateTheResult() {
            setResultState((current)=>({
                    ...current,
                    loading: true
                }));
            let data;
            let response;
            let error;
            try {
                [response, data] = await executeRequest();
            } catch (err) {
                error = err;
            } finally{
                if (true === abortController.signal.aborted) return;
                setResultState({
                    loading: false,
                    response,
                    data,
                    error
                });
            }
        }
        updateTheResult();
        return ()=>{
            abortController.abort('Request changed');
        };
    }, [
        requestState.request
    ]);
    function abortRequest() {
        if (void 0 === requestState.request) return;
        requestState.request.abortController.abort();
        setResultState((current)=>({
                ...current,
                loading: false
            }));
    }
    return {
        requestDeps,
        currentResult,
        setRequestState,
        abortRequest
    };
}
function useSharedPolling(currentResult, refetch, options) {
    let pollInterval = options?.pollInterval;
    if (options?.pause === true || currentResult.loading || void 0 !== currentResult.error) pollInterval = void 0;
    usePolling(refetch, pollInterval);
    let errorToRetry = currentResult.error;
    if (options?.pause === true || options?.autoRetryErrors !== true) errorToRetry = void 0;
    return useRetryWithBackoff(refetch, currentResult.loading, errorToRetry);
}
function useFetch(url, init, options) {
    const { requestDeps, currentResult, setRequestState, abortRequest } = useSharedRequestDeps(url, init, options);
    const refetch = useCallback(()=>{
        const request = createRequest(url, init, options?.fetchFn);
        setRequestState({
            request,
            requestDeps
        });
    }, requestDeps);
    const secondsUntilRetry = useSharedPolling(currentResult, refetch, options);
    return [
        {
            ...currentResult,
            secondsUntilRetry
        },
        refetch,
        abortRequest
    ];
}
function useLazyFetch(url, init, options) {
    const pausedOptions = {
        ...options,
        pause: true
    };
    const { requestDeps, currentResult, setRequestState, abortRequest } = useSharedRequestDeps(url, init, pausedOptions);
    const execute = useCallback((params)=>{
        let newUrl = url;
        const newInit = {
            ...init
        };
        if (void 0 !== params) {
            if (init?.method === void 0 || init?.method === 'GET') {
                const path = url.split('?')[0];
                if ('string' == typeof params) newUrl = `${path}?${params}`;
                else if (params instanceof URLSearchParams) newUrl = `${path}?${params.toString()}`;
                else throw new Error('Invalid params for GET request - params must be a string or instance of URLSearchParams');
            } else if (init?.method === 'POST' || init?.method === 'PUT' || init?.method === 'DELETE') newInit.body = params;
        }
        const request = createRequest(newUrl, newInit, options?.fetchFn);
        setRequestState({
            request,
            requestDeps
        });
    }, requestDeps);
    const secondsUntilRetry = useSharedPolling(currentResult, execute, pausedOptions);
    return [
        {
            ...currentResult,
            secondsUntilRetry
        },
        execute,
        abortRequest
    ];
}
function createRequest(url, init, fetchFn) {
    const fetch = fetchFn ?? self.fetch;
    const abortController = new AbortController();
    init = {
        ...init,
        signal: abortController.signal
    };
    async function executeRequest() {
        const response = await fetch(url, init);
        const data = await response.json();
        return [
            response,
            data
        ];
    }
    return {
        abortController,
        executeRequest
    };
}
export { useFetch, useLazyFetch };

//# sourceMappingURL=fetch.js.map